// i18next-extract-mark-ns-start contact-page

import React, { FC, useLayoutEffect, useRef } from 'react'
import { graphql } from 'gatsby'
import { gsap, ScrollTrigger } from 'gsap/all'
import { useTranslation } from 'gatsby-plugin-react-i18next'
import HeroSection from 'src/components/sections/hero'

if (typeof window !== 'undefined') {
  gsap.registerPlugin(ScrollTrigger)
}
import Layout from 'src/components/layout'
import BasicTeaserSection from 'src/components/sections/teaser/basic-teaser'
import ContactSection from 'src/components/sections/ContactSection'
import HighLight from 'src/components/sections/teaser/Highlight'
import ContactActionButton from 'src/components/ui/contact/ContactActionButton'
import ContactLocationBox from 'src/components/ui/contact/ContactLocationBox'
import ContactForm from 'src/components/ui/contact/ContactForm'
import { directusFileUrl } from 'src/helpers/directus'
import Subtitle from 'src/components/ui/Subtitle'
import H2 from 'src/components/ui/title/H2'
import { getPageContent } from 'src/helpers/pageContent'

interface IContactPageProps {
  className?: string
  data: {
    directus: {
      pages: Array<{ translations: Array<{ content: string }> }>
    }
  }
  pageContext?: {
    language?: string
  }
}

const IS_WARSAW_ADDRESS_HIDDEN = true

const ContactPage: FC<IContactPageProps> = ({ className, data, pageContext }) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const language = pageContext?.language || 'en'
  const translation = data?.directus?.pages?.[0]?.translations?.[0]
  const cmsJson = getPageContent('/contact', language, translation?.content)

  useLayoutEffect(() => {
    if (typeof window === 'undefined') return

    const items = gsap.utils.toArray('.reveal-item')

    items.forEach((item: any) => {
      gsap.fromTo(
        item,
        {
          y: 40,
          opacity: 0,
        },
        {
          y: 0,
          opacity: 1,
          duration: 1,
          ease: 'power3.out',
          scrollTrigger: {
            trigger: item,
            start: 'top 95%',
            toggleActions: 'play none none none',
          },
        }
      )
    })

    return () => {
      ScrollTrigger.getAll().forEach((t) => t.kill())
    }
  }, [])

  if (!translation) {
    return <h1>Missing translation</h1>
  }

  const { t } = useTranslation('contact-page')

  return (
    <Layout title={cmsJson.title} metas={cmsJson.metas}>
      <div className={className} ref={containerRef}>
        <HeroSection
          isFullHeight={true}
          alignItems="center"
          sectionClassName="mt-4 md:mt-8 lg:mt-0"
          showMagneticButton={false}
          showIso={false}
          translationContent={{
            subtitle: t('subtitle'),
            firstPartOfTitle: t('title-first-part'),
            secondPartOfTitle: t('title-second-part'),
            sectionName: 'contact',
            ctaLink: '',
            ctaText: '',
            content: t('hero-description'),
          }}
          isFullWidthDescription={true}
        >
          <ContactForm options={cmsJson.contactOptions} />
        </HeroSection>

        <BasicTeaserSection
          layoutVariant="split"
          hideDefaultHeader={false}
          className="reveal-item opacity-0"
          imageLink={cmsJson.wroclawMapUrl}
          imageOverlayLabel={t('see on map')}
          previewImageUrl="/images/placeholders/illustration/concordia.webp"
          imageChildren={
            <div
              className="p-6 rounded-2xl text-white shadow-2xl"
              style={{
                backgroundColor: 'rgba(0, 0, 0, 0.1)',
                backdropFilter: 'blur(12px)',
                WebkitBackdropFilter: 'blur(12px)',
              }}
            >
              <h4 className="text-2xl font-bold">{t('wroclaw-address-1')}</h4>
              <p className="text-lg opacity-90 mt-2">{t('wroclaw-address-2')}</p>
              <p className="text-lg opacity-90">{t('wroclaw-address-3')}</p>
            </div>
          }
          translationContent={{
            subtitle: t('visit-us'),
            firstPartOfTitle: t('we-have'),
            secondPartOfTitle: t('two-locations'),
            sectionName: 'contact-wroclaw',
            image: {
              url: directusFileUrl(cmsJson.wroclawImageId),
              alt: t('Wroclaw office'),
            },
          }}
        >
          <div className="flex flex-col gap-6">
            <HighLight className="mt-4" isFullWidth={true}>
              <b>{t('location-description')}</b>
            </HighLight>
          </div>
        </BasicTeaserSection>

        {!IS_WARSAW_ADDRESS_HIDDEN && (
          <BasicTeaserSection
            layoutVariant="split"
            hideDefaultHeader={true}
            className="reveal-item opacity-0 -mt-20 md:-mt-32"
            imageLink={cmsJson.warsawMapUrl}
            imageOverlayLabel={t('see on map')}
            imageChildren={
              <div
                className="p-6 rounded-2xl text-white shadow-2xl"
                style={{
                  backgroundColor: 'rgba(0, 0, 0, 0.2)',
                  backdropFilter: 'blur(24px)',
                  WebkitBackdropFilter: 'blur(24px)',
                }}
              >
                <h4 className="text-2xl font-bold">{t('warszawa-address-1')}</h4>
                <p className="text-lg opacity-90 mt-2">{t('warszawa-address-2')}</p>
                <p className="text-lg opacity-90">{t('warszawa-address-3')}</p>
              </div>
            }
            translationContent={{
              subtitle: '',
              firstPartOfTitle: '',
              secondPartOfTitle: '',
              sectionName: 'contact-warsaw',
              image: {
                url: directusFileUrl(cmsJson.warsawImageId),
                alt: t('Warsaw office'),
              },
            }}
          >
            <div className="flex flex-col gap-6">
              <Subtitle>{t('Warsaw HQ')}</Subtitle>
              <H2 firstPart={t('Our')} secondPart={t('Warsaw Office')} />
            </div>
          </BasicTeaserSection>
        )}

        <ContactSection />
      </div>
    </Layout>
  )
}

ContactPage.defaultProps = {
  className: '',
}

export const query = graphql`
  query ($language: String!) {
    locales: allLocale(filter: { language: { eq: $language } }) {
      edges {
        node {
          ns
          data
          language
        }
      }
    }
    directus {
      pages(filter: { slug: { _eq: "/contact" } }) {
        translations(filter: { languages_code: { code: { _eq: $language } } }) {
          content
        }
      }
    }
  }
`

export default ContactPage
